import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

export type PlanTier = 'Personal' | 'Plus' | 'Pro';

type PlanLimits = {
  maxProfiles: number | 'unlimited';
  maxAccounts: number | 'unlimited';
  maxCards: number | 'unlimited';
};

const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  Personal: { maxProfiles: 1, maxAccounts: 2, maxCards: 'unlimited' },
  Plus: { maxProfiles: 3, maxAccounts: 5, maxCards: 'unlimited' },
  Pro: { maxProfiles: 'unlimited', maxAccounts: 'unlimited', maxCards: 'unlimited' },
};

// Price IDs para cada plano
export const PLAN_PRICES = {
  Personal: {
    monthly: 'price_1RwjroHsagPmSTysN9zCam64',
    yearly: 'price_1S9zcqHsagPmSTysm0jo5Dlb'
  },
  Plus: {
    monthly: 'price_1RwjtKHsagPmSTysHBQnMxyQ',
    yearly: 'price_1S9zfGHsagPmSTyskQRR2OpB'
  },
  Pro: {
    monthly: 'price_1RwjuoHsagPmSTys6VC9WfpA',
    yearly: 'price_1S9zg3HsagPmSTysLKVvaSrL'
  }
};

interface SubscriptionContextType {
  tier: PlanTier;
  subscribed: boolean;
  isLoading: boolean;
  planLimits: PlanLimits;
  subscriptionEnd: string | null;
  needsTrial: boolean;
  isTrial: boolean;
  trialEnd: string | null;
  createCheckoutSession: (priceId: string) => Promise<void>;
  createPortalSession: () => Promise<void>;
  checkSubscription: (forceRefresh?: boolean) => Promise<void>;
  setTier: (tier: PlanTier) => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tier, setTierState] = useState<PlanTier>('Personal');
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [needsTrial, setNeedsTrial] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const planLimits = useMemo(() => PLAN_LIMITS[tier], [tier]);

  const checkSubscription = async (forceRefresh = false) => {
    if (!user) {
      setIsLoading(false);
      return;
    }
    
    logger.info('[SubscriptionContext] Starting checkSubscription', { forceRefresh, userId: user.id });
    setIsLoading(true);
    
    try {
      // Check if user is admin - admins bypass subscription checks
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (roleData) {
        logger.info('[SubscriptionContext] Admin user detected, bypassing subscription check');
        setTierState('Pro');
        setSubscribed(true);
        setSubscriptionEnd(null);
        setNeedsTrial(false);
        setIsTrial(false);
        setTrialEnd(null);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        body: { forceRefresh }
      });
      
      if (error) {
        logger.error('[Subscription] Error checking subscription:', error);
        toast.error('Erro ao verificar assinatura');
        return;
      }

      if (data) {
        logger.info('[SubscriptionContext] Received subscription data:', {
          subscribed: data.subscribed,
          tier: data.subscription_tier,
          isTrial: data.is_trial,
          trialEnd: data.trial_end
        });
        const tier = data.subscription_tier as PlanTier || 'Plus';
        setTierState(tier);
        setSubscribed(data.subscribed);
        setSubscriptionEnd(data.subscription_end);
        setNeedsTrial(data.needsTrial || false);
        setIsTrial(data.is_trial || false);
        setTrialEnd(data.trial_end || null);
        
        // Apenas mostrar feedback se assinatura estiver acabando
        if (data.subscribed && data.subscription_end) {
          // Verificar se a assinatura está acabando (menos de 7 dias)
          const endDate = new Date(data.subscription_end);
          const now = new Date();
          const daysUntilEnd = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilEnd <= 7 && daysUntilEnd > 0 && !forceRefresh) {
            toast.warning(`Sua assinatura expira em ${daysUntilEnd} ${daysUntilEnd === 1 ? 'dia' : 'dias'}`);
          } else if (daysUntilEnd <= 0 && !forceRefresh) {
            toast.error('Sua assinatura expirou');
          }
        }
      }
    } catch (e) {
      logger.error('[SubscriptionContext] Unexpected error checking subscription:', e);
      toast.error('Erro ao verificar assinatura');
    } finally {
      logger.info('[SubscriptionContext] Finished checkSubscription', { subscribed, tier });
      setIsLoading(false);
    }
  };

  const createCheckoutSession = async (priceId: string) => {
    if (!user) {
      toast.error('Você precisa estar logado para assinar');
      return;
    }

    logger.info('[Subscription] Creating checkout session for price:', priceId);
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId }
      });

      logger.info('[Subscription] Checkout response:', { data, error });

      if (error) {
        logger.error('[Subscription] Error creating checkout:', error);
        toast.error('Erro ao criar sessão de pagamento');
        return;
      }

      if (data?.url) {
        logger.info('[Subscription] Redirecting to:', data.url);
        // Usar window.location.href ao invés de window.open para evitar bloqueio de pop-ups
        window.location.href = data.url;
      } else {
        logger.error('[Subscription] No URL returned from checkout session');
        toast.error('Erro: URL de pagamento não foi gerada');
      }
    } catch (e) {
      logger.error('[Subscription] Unexpected error creating checkout:', e);
      toast.error('Erro ao criar sessão de pagamento');
    } finally {
      setIsLoading(false);
    }
  };

  const createPortalSession = async () => {
    if (!user) {
      toast.error('Você precisa estar logado');
      return;
    }

    logger.info('[Subscription] Creating portal session');
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: {
          returnUrl: `${window.location.origin}/configuracoes`
        }
      });

      logger.info('[Subscription] Portal response:', { data, error });

      if (error) {
        logger.error('[Subscription] Error creating portal:', error);
        const errorMessage = error.message || 'Erro ao acessar portal de assinatura';
        toast.error(errorMessage);
        return;
      }

      if (data?.url) {
        logger.info('[Subscription] Redirecting to portal:', data.url);
        // Usar window.location.href para redirecionamento mais confiável
        window.location.href = data.url;
      } else {
        logger.error('[Subscription] No URL returned from portal session');
        toast.error('Erro: URL do portal não foi gerada');
      }
    } catch (e) {
      logger.error('[Subscription] Unexpected error creating portal:', e);
      toast.error('Erro ao acessar portal de assinatura');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkSubscription();
    }
  }, [user]);

  // Auto-refresh subscription status every 5 minutes (reduced from 30s)
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      checkSubscription();
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(interval);
  }, [user]);

  const setTier = async (newTier: PlanTier) => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('subscribers')
        .upsert({
          user_id: user.id,
          email: user.email,
          subscription_tier: newTier,
          updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' });
      if (error) throw error;
      setTierState(newTier);
    } catch (e) {
      logger.error('[Subscription] Error setting tier:', e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SubscriptionContext.Provider value={{ 
      tier, 
      subscribed, 
      isLoading, 
      planLimits, 
      subscriptionEnd,
      needsTrial,
      isTrial,
      trialEnd,
      createCheckoutSession,
      createPortalSession,
      checkSubscription,
      setTier,
      refreshSubscription: checkSubscription 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within a SubscriptionProvider');
  return ctx;
};
